import React, { useEffect, useRef, useState } from "react";
import api, { errMsg } from "../api";
import { useAuth } from "../AuthContext";
import { Link } from "react-router-dom";
import { Play, Square, Loader2, Clock, MapPin, Home as HomeIcon, Briefcase, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import GeoConsentDialog, { useGeoConsent } from "./GeoConsentDialog";

const AUTO_CHECKOUT_SECONDS = 15;

function fmtDuration(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Big eye-catching Stechuhr-Hero. Replaces the small widget.
 * Lets the user clock in / clock out directly from the dashboard.
 * Clock-out goes through a 15-second confirmation countdown — if no action,
 * the auto-confirm fires.
 */
export default function TimeClockHero({ lang = "de" }) {
    const { user } = useAuth();
    const geo = useGeoConsent();
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [locations, setLocations] = useState([]);
    const [settings, setSettings] = useState(null);
    const [picked, setPicked] = useState("");          // location_id or "home_office" or "outside"
    const [busy, setBusy] = useState(false);
    const [tick, setTick] = useState(0);               // re-render every second for the running timer
    const [confirmOut, setConfirmOut] = useState(false);

    const load = React.useCallback(async () => {
        try {
            const promises = [
                api.get("/timetrack/active").catch(() => ({ data: null })),
                api.get("/timetrack/locations").catch(() => ({ data: [] })),
            ];
            if (user?.id) {
                promises.push(api.get(`/timetrack/users/${user.id}/settings`).catch(() => ({ data: null })));
            } else {
                promises.push(Promise.resolve({ data: null }));
            }
            const [a, l, s] = await Promise.all(promises);
            setActive(a.data || null);
            setLocations(Array.isArray(l.data) ? l.data : []);
            setSettings(s.data || null);
        } finally { setLoading(false); }
    }, [user?.id]);
    useEffect(() => { load(); }, [user?.id, load]);
    // 1Hz timer for the running clock
    useEffect(() => {
        if (!active) return;
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [active]);

    // Default location pick — first allowed → home_office → first office
    useEffect(() => {
        if (active || picked) return;
        const allowed = (settings?.allowed_location_ids || []);
        if (allowed.length > 0) setPicked(allowed[0]);
        else if (settings?.home_office) setPicked("home_office");
        else if (locations[0]) setPicked(locations[0].id);
    }, [active, settings, locations, picked]);

    const getGps = () =>
        new Promise((resolve) => {
            if (!navigator.geolocation) return resolve({ lat: null, lng: null });
            navigator.geolocation.getCurrentPosition(
                (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                () => resolve({ lat: null, lng: null }),
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
            );
        });

    const doClockIn = async () => {
        if (!picked) { toast.error(lang === "de" ? "Bitte Standort wählen" : "Pick a location"); return; }
        // Gate behind GPS consent (per device, once)
        const ok = await geo.ensure();
        if (!ok) {
            toast.error(lang === "de"
                ? "Ohne Standort-Einwilligung kein Einstempeln möglich"
                : "Cannot clock in without location consent");
            return;
        }
        setBusy(true);
        try {
            const gps = await getGps();
            const payload =
                picked === "home_office" ? { location_type: "home_office", lat: gps.lat, lng: gps.lng } :
                picked === "outside"     ? { location_type: "outside",     lat: gps.lat, lng: gps.lng } :
                                           { location_type: "office", location_id: picked, lat: gps.lat, lng: gps.lng };
            const { data } = await api.post("/timetrack/clock-in", payload);
            setActive(data);
            toast.success(lang === "de" ? "Eingestempelt ✓" : "Clocked in ✓");
            window.dispatchEvent(new CustomEvent("mytacs:tick"));
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
        finally { setBusy(false); }
    };
    const doClockOut = async () => {
        const ok = await geo.ensure();
        if (!ok) {
            toast.error(lang === "de"
                ? "Ohne Standort-Einwilligung kein Ausstempeln möglich"
                : "Cannot clock out without location consent");
            setConfirmOut(false);
            return;
        }
        setBusy(true);
        try {
            const gps = await getGps();
            await api.post("/timetrack/clock-out", { lat: gps.lat, lng: gps.lng });
            setActive(null);
            toast.success(lang === "de" ? "Ausgestempelt ✓" : "Clocked out ✓");
            window.dispatchEvent(new CustomEvent("mytacs:tick"));
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
        finally { setBusy(false); setConfirmOut(false); }
    };

    if (loading) {
        return (
            <div className="rounded-2xl border border-neutral-200 bg-white p-5 mb-4 flex items-center gap-3" data-testid="tt-hero-loading">
                <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
                <span className="text-sm text-neutral-500">{lang === "de" ? "Lade Zeiterfassung…" : "Loading…"}</span>
            </div>
        );
    }

    // ── Not clocked in ──
    if (!active) {
        const allowedLocs = locations.filter((l) => (settings?.allowed_location_ids || []).includes(l.id));
        const opts = [
            ...allowedLocs.map((l) => ({ id: l.id, label: l.name, icon: <Briefcase className="w-3.5 h-3.5" /> })),
            ...(settings?.home_office ? [{ id: "home_office", label: lang === "de" ? "Homeoffice" : "Home office", icon: <HomeIcon className="w-3.5 h-3.5" /> }] : []),
            ...(settings?.allow_outside ? [{ id: "outside", label: lang === "de" ? "Außendienst" : "Outside", icon: <MapPin className="w-3.5 h-3.5" /> }] : []),
        ];
        return (
            <>
                <div className="rounded-2xl border-2 border-dashed border-neutral-300 bg-gradient-to-br from-neutral-50 to-white p-4 sm:p-6 mb-4" data-testid="tt-hero-inactive">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 text-neutral-500 mb-1">
                                <Clock className="w-4 h-4" />
                                <span className="text-[10px] uppercase tracking-wider font-semibold">{lang === "de" ? "Stechuhr" : "Time clock"}</span>
                            </div>
                            <p className="font-display text-2xl sm:text-3xl tracking-tight text-neutral-900">
                                {lang === "de" ? "Nicht eingestempelt" : "Not clocked in"}
                            </p>
                            <p className="text-sm text-neutral-500 mt-1">{lang === "de" ? "Wähle einen Standort und starte deinen Arbeitstag." : "Pick a location and start your day."}</p>
                            {geo.consent && !geo.consent.accepted && (
                                <button onClick={() => geo.setAskOpen(true)} className="mt-2 text-[11px] text-amber-700 underline" data-testid="geo-consent-reopen">
                                    {lang === "de" ? "⚠ Standort-Einwilligung erforderlich — jetzt erteilen" : "⚠ Location consent required — review now"}
                                </button>
                            )}
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch gap-2 w-full md:w-auto">
                            <select
                                value={picked}
                                onChange={(e) => setPicked(e.target.value)}
                                className="h-12 px-3 border border-neutral-300 rounded-lg bg-white text-sm font-medium min-w-[180px]"
                                data-testid="tt-hero-location"
                            >
                                {opts.length === 0 && <option value="">{lang === "de" ? "Kein Standort verfügbar" : "No location"}</option>}
                                {opts.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                            <Button
                                onClick={doClockIn}
                                disabled={busy || !picked}
                                className="h-12 px-6 bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800 text-white font-semibold shadow-lg shadow-emerald-500/30"
                                data-testid="tt-hero-clockin"
                            >
                                {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Play className="w-5 h-5 mr-2 fill-white" />}
                                {lang === "de" ? "Einstempeln" : "Clock in"}
                            </Button>
                        </div>
                    </div>
                </div>
                <GeoConsentDialog open={geo.askOpen} onAccept={geo.accept} onDecline={geo.decline} lang={lang} />
            </>
        );
    }

    // ── Active ──
    const startedMs = new Date(active.started_at).getTime();
    const elapsedMs = Date.now() - startedMs;
    const onBreak = active.on_break;
    return (
        <>
            <div
                className={`rounded-2xl border-2 p-4 sm:p-6 mb-4 transition shadow-lg ${
                    onBreak ? "bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-300 shadow-amber-200/40"
                            : "bg-gradient-to-br from-emerald-50 to-emerald-100/40 border-emerald-300 shadow-emerald-200/40"
                }`}
                data-testid="tt-hero-active"
            >
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                    <div className="flex-1 min-w-0">
                        <div className={`flex items-center gap-2 mb-1 ${onBreak ? "text-amber-700" : "text-emerald-700"}`}>
                            <span className="relative flex h-2.5 w-2.5">
                                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${onBreak ? "bg-amber-400" : "bg-emerald-400"} opacity-75`} />
                                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${onBreak ? "bg-amber-500" : "bg-emerald-500"}`} />
                            </span>
                            <span className="text-[10px] uppercase tracking-wider font-bold">
                                {onBreak ? (lang === "de" ? "In Pause" : "On break") : (lang === "de" ? "Eingestempelt" : "Clocked in")}
                            </span>
                            {active.location_name && <span className="text-xs">· {active.location_name}</span>}
                        </div>
                        <p className={`font-mono text-3xl sm:text-5xl tracking-tight font-bold tabular-nums ${onBreak ? "text-amber-800" : "text-emerald-800"}`} data-testid="tt-hero-timer">
                            {fmtDuration(elapsedMs)}
                        </p>
                        <p className={`text-xs mt-1 ${onBreak ? "text-amber-700/80" : "text-emerald-700/80"}`}>
                            {lang === "de" ? "Seit" : "Since"} {new Date(active.started_at).toLocaleTimeString(lang === "de" ? "de-DE" : "en-GB", { hour: "2-digit", minute: "2-digit" })}
                            {(active.breaks || []).length > 0 && <> · {(active.breaks || []).length} {lang === "de" ? "Pause(n)" : "break(s)"}</>}
                        </p>
                    </div>
                    <div className="flex items-stretch gap-2 w-full md:w-auto">
                        <Link to="/zeiterfassung" className="h-12 inline-flex items-center justify-center px-4 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 text-sm font-medium text-neutral-700" data-testid="tt-hero-open">
                            {lang === "de" ? "Pause / Details" : "Break / details"}
                        </Link>
                        <Button
                            onClick={() => setConfirmOut(true)}
                            disabled={busy}
                            className="h-12 px-5 bg-gradient-to-br from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 text-white font-semibold shadow-lg shadow-red-500/30"
                            data-testid="tt-hero-clockout"
                        >
                            <Square className="w-4 h-4 mr-2 fill-white" /> {lang === "de" ? "Ausstempeln" : "Clock out"}
                        </Button>
                    </div>
                </div>
            </div>
            <ClockOutCountdownDialog
                open={confirmOut}
                onCancel={() => setConfirmOut(false)}
                onConfirm={doClockOut}
                lang={lang}
                elapsed={fmtDuration(elapsedMs)}
            />
            <GeoConsentDialog open={geo.askOpen} onAccept={geo.accept} onDecline={geo.decline} lang={lang} />
        </>
    );
}

/** Confirmation with a 15s auto-confirm countdown. Cancel resets state. */
function ClockOutCountdownDialog({ open, onConfirm, onCancel, lang, elapsed }) {
    const [seconds, setSeconds] = useState(AUTO_CHECKOUT_SECONDS);
    const ranRef = useRef(false);
    useEffect(() => {
        if (!open) { setSeconds(AUTO_CHECKOUT_SECONDS); ranRef.current = false; return; }
        ranRef.current = false;
        setSeconds(AUTO_CHECKOUT_SECONDS);
        const id = setInterval(() => {
            setSeconds((s) => {
                if (s <= 1) {
                    clearInterval(id);
                    if (!ranRef.current) { ranRef.current = true; onConfirm(); }
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [open, onConfirm]);

    const pct = Math.round(((AUTO_CHECKOUT_SECONDS - seconds) / AUTO_CHECKOUT_SECONDS) * 100);
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
            <DialogContent className="max-w-md" data-testid="tt-hero-confirm-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        {lang === "de" ? "Wirklich ausstempeln?" : "Clock out?"}
                    </DialogTitle>
                    <DialogDescription>
                        {lang === "de"
                            ? `Du arbeitest seit ${elapsed}. Wenn du nichts tust, wird in ${seconds}s automatisch ausgestempelt.`
                            : `You've been working for ${elapsed}. Will auto clock-out in ${seconds}s if no action.`}
                    </DialogDescription>
                </DialogHeader>
                <div className="h-2 rounded-full bg-neutral-200 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-400 to-red-500 transition-all duration-1000 ease-linear" style={{ width: `${pct}%` }} />
                </div>
                <div className="text-center font-mono text-3xl font-bold text-neutral-800 tabular-nums" data-testid="tt-hero-countdown">{seconds}s</div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onCancel} className="flex-1 h-11" data-testid="tt-hero-cancel">
                        {lang === "de" ? "Doch noch eingestempelt bleiben" : "Stay clocked in"}
                    </Button>
                    <Button onClick={() => { ranRef.current = true; onConfirm(); }} className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white" data-testid="tt-hero-confirm">
                        {lang === "de" ? "Jetzt ausstempeln" : "Clock out now"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
